import { createContext, useContext, type ReactNode } from 'react';
import { useUnifiedConfigLoader, type LoadingStage } from '@/shared/hooks/useUnifiedConfigLoader';
import type { SystemSettings } from './SystemSettingsContext';

/**
 * Config Loader context value
 * Provides access to the unified configuration loading state
 */
export interface ConfigLoaderContextValue {
  /** Current loading stage */
  stage: LoadingStage;

  /** Is configuration ready? */
  isReady: boolean;

  /** Is loading in progress? */
  isLoading: boolean;

  /** System settings (null if not loaded or emergency fallback) */
  settings: SystemSettings | null;

  /** Error if loading failed */
  error: Error | null;

  /** User-friendly stage message */
  stageMessage: string;
}

/**
 * Config Loader context
 * Shares the unified configuration loading state across the app
 */
export const ConfigLoaderContext = createContext<ConfigLoaderContextValue | undefined>(undefined);

export interface ConfigLoaderProviderProps {
  children: ReactNode;
}

/**
 * Config Loader Provider Component
 * Wraps the app and provides unified configuration loading state
 */
export const ConfigLoaderProvider = ({ children }: ConfigLoaderProviderProps) => {
  const configState = useUnifiedConfigLoader();

  return (
    <ConfigLoaderContext.Provider value={configState}>
      {children}
    </ConfigLoaderContext.Provider>
  );
};

/**
 * Hook to access config loader context
 * @throws Error if used outside ConfigLoaderProvider
 */
export const useConfigLoader = (): ConfigLoaderContextValue => {
  const context = useContext(ConfigLoaderContext);

  if (!context) {
    throw new Error('useConfigLoader must be used within a ConfigLoaderProvider');
  }

  return context;
};
